/* eslint-disable react/no-danger */
import { createElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ClassNames from '@streamloots/classnames';
import { PageFeed } from 'model/indexTS';
import { TranslateInterface } from 'utils/translation';
import { FeedAttachments } from './FeedAttachments';
import { ObfuscatedFeed } from './ObfuscatedFeed';
import theme from './feed-list.scss';

const classNames = ClassNames(theme);

export interface FeedContentProps extends TranslateInterface {
  feed: PageFeed;
}

const formatStringToCamelCase = (str: string) => {
  const splitted = str.split('-');
  if (splitted.length === 1) {
    return splitted[0];
  }

  return (
    splitted[0] +
    splitted
      .slice(1)
      .map(word => word[0].toUpperCase() + word.slice(1))
      .join('')
  );
};

export const getStyleObjectFromString = (str: string) => {
  const style = {};
  str.split(';').forEach(el => {
    const [property, value] = el.split(':');
    if (!property) {
      return;
    }

    const formattedProperty = formatStringToCamelCase(property.trim());
    style[formattedProperty] = value.trim();
  });

  return style;
};

const parseNodeToJSX = (node: HTMLElement, children: ReactNode[] = []): JSX.Element | null => {
  const nodeName = node.nodeName.toLowerCase();
  const style = getStyleObjectFromString(node.getAttribute('style') ?? '');
  const attr: Record<string, any> = {};
  const url = node.getAttribute('href');
  if (nodeName === 'a') {
    let path: string | undefined;
    if (url?.startsWith('/')) {
      path = url;
    }

    if (path) {
      return createElement(
        Link,
        {
          to: path,
          style,
        },
        ...children,
      );
    }

    attr.href = url;
  }

  return createElement(
    nodeName,
    {
      style,
      ...attr,
    },
    ...children,
  );
};

const convertNodesToJsx = (node: ChildNode): ReactNode[] => {
  switch (node.nodeType) {
    case 1: {
      const htmlElement = node as HTMLElement;

      if (!htmlElement.hasChildNodes()) {
        return [parseNodeToJSX(htmlElement)];
      }
      const nodes = [] as ReactNode[];

      node.childNodes.forEach(internalNode => {
        nodes.push(...convertNodesToJsx(internalNode));
      });

      return [parseNodeToJSX(htmlElement, nodes)];
    }
    case 3: // Text node
      return [node.nodeValue];
    default:
      return [];
  }
};

export const FeedContent = ({ feed, t }: FeedContentProps): JSX.Element => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const elements = useMemo(() => {
    const reactElements = [] as any[];

    if (!isClient) {
      return reactElements;
    }

    const contentAsDom = document.createElement('div');
    contentAsDom.innerHTML = feed.content;
    contentAsDom.childNodes.forEach(node => {
      reactElements.push(...convertNodesToJsx(node));
    });
    return reactElements;
  }, [feed.content, isClient]);

  if (!feed.visible) {
    return <ObfuscatedFeed t={t} feed={feed} />;
  }

  return (
    <>
      {isClient ? (
        <>{elements.length > 0 && <div className={classNames('feed-detail__content')}>{elements}</div>}</>
      ) : (
        <>
          {feed.content && (
            <div className={classNames('feed-detail__content')} dangerouslySetInnerHTML={{ __html: feed.content }} />
          )}
        </>
      )}

      <FeedAttachments t={t} feed={feed} />
    </>
  );
};
